/* eslint-disable no-restricted-imports */
import React, {useEffect, useMemo, useRef} from 'react'
import {UIGameActionButton} from '../../../../common/UIGameActionButton'
import {
    UIGameResourceCardsTooltipContent,
    UIGameResourceCardsTooltipContentHandle,
} from '../../../../common/UIGameResourceCardsTooltipContent'
import {useActionButtonCommonErrors} from '../../utils'
import {Tooltip} from '../../../../common/Tooltip'
import {T} from '../../../../common/T'
import {ActionState, CardEnum} from '@colonist/game-states'
import {useGameStore} from '../../../../store/GameStoreContext'
import {usePlayerValidators} from '../../../../store/selectors/usePlayerValidators'
import {ActionStatus} from '@colonist/game-base-types'
import {MapValidator, MechanicValidatorKnightPlayer} from '@colonist/game-mechanic-validators'
import {TranslationObject} from '@colonist/i18n'
import {KNIGHT_ACTIVATION_IMAGE_PATH} from './utils'
import {useUIGameSocketContext} from '../../../../contexts/UIGameSocketContext'
import {useCurrentStateValidator} from '../../../../store/selectors/useCurrentStateValidator'

const cardToActivateKnight = [CardEnum.Grain]

export const UIGameActionButtonKnightActivation = ({
    className,
    onDisabledChange,
}: {
    className?: string
    onDisabledChange: (isDisabled: boolean) => void
}) => {
    const myColor = useGameStore((state) => state.myColor)
    const mechanicKnightState = useGameStore((state) => state.gameState.mechanicKnightState)
    const mapState = useGameStore((state) => state.gameState.mapState)
    const currentStateValidator = useCurrentStateValidator()
    const {getPlayerValidator, playerValidators} = usePlayerValidators()
    const playerValidator = getPlayerValidator(myColor)
    const tooltipRef = useRef<UIGameResourceCardsTooltipContentHandle>(null)
    const {errorHelperText, hasCommonErrors} = useActionButtonCommonErrors()
    const {sendActivateKnight, sendCancelAction, sendClearCornerHighlight} = useUIGameSocketContext()
    const {actionStatus, knightsEligibleForActivationCount}: {
        actionStatus: ActionStatus
        knightsEligibleForActivationCount: number
    } = useMemo(() => {
        const knightPlayerState = mechanicKnightState?.[myColor]

        if(!knightPlayerState) {
            return {actionStatus: {canTakeAction: false}, knightsEligibleForActivationCount: 0}
        }
        const knightPlayerValidator = new MechanicValidatorKnightPlayer(playerValidator,
            new MapValidator(mapState), knightPlayerState)
        const knightsEligibleForActivationCountLocal = knightPlayerValidator.getAmountOfKnightsEligibleForActivation()
        const canActivateKnightActionStatus = knightPlayerValidator.canPlayerActivateKnight()
        return {
            actionStatus: canActivateKnightActionStatus,
            knightsEligibleForActivationCount: knightsEligibleForActivationCountLocal,
        }
    }, [
        playerValidators,
        mapState,
        mechanicKnightState,
        myColor,
    ])

    const triggerFlickerCardsAnimation = () => {
        const notEnoughResources = !playerValidator.hasResourceCardsInHand(cardToActivateKnight)
        if(notEnoughResources) {
            tooltipRef.current?.triggerFlickerAnimation()
        }
    }

    const disabled = hasCommonErrors || !actionStatus.canTakeAction
    const actionState = currentStateValidator.getPlayerActionState(myColor)
    const isActivatingKnight = actionState === ActionState.ActivateKnight && !disabled
    const icon = KNIGHT_ACTIVATION_IMAGE_PATH[myColor]
    const helperText: TranslationObject | undefined = errorHelperText || actionStatus.errorMessageIfCantTakeAction

    useEffect(() => {
        onDisabledChange(disabled)
    }, [disabled])

    const onClick = () => {
        if(disabled) return
        if(isActivatingKnight) {
            sendClearCornerHighlight()
            sendCancelAction()
        } else {
            sendActivateKnight()
        }
    }

    return <Tooltip
        position='bottom'
        title={<T id='strings:game.tooltips.activateKnight'/>}>
        <UIGameActionButton
            className={className}
            count={knightsEligibleForActivationCount}
            countColor={myColor}
            disabled={disabled}
            helperText={helperText}
            highlight={isActivatingKnight}
            iconPath={icon}
            onClickActionIfDisabled={triggerFlickerCardsAnimation}
            tooltipCustomMenuComponent={
                <UIGameResourceCardsTooltipContent
                    cardInventory={playerValidator.resourceCards.cards}
                    cardsToBuild={cardToActivateKnight}
                    ref={tooltipRef}/>
            }
            onClick={onClick}
        />
    </Tooltip>
}
